import React, { useEffect, useState } from 'react';

/**
 * Composant pour basculer entre le thème clair et sombre avec une interface moderne
 * utilisant les icônes Material Design
 * @returns {JSX.Element} Bouton de basculement du thème
 */
const ThemeSwitcher = () => {
  // État pour suivre le thème actuel
  const [darkMode, setDarkMode] = useState(false);

  // Initialiser le thème à partir des préférences enregistrées
  useEffect(() => {
    // Vérifier s'il y a un thème stocké dans localStorage
    const savedTheme = localStorage.getItem('theme');
    let isDarkMode;
    
    if (savedTheme === 'dark') {
      isDarkMode = true;
    } else if (savedTheme === 'light') {
      isDarkMode = false;
    } else {
      // Si aucun thème n'est stocké, vérifier les préférences système
      isDarkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
      // Enregistrer la préférence initiale
      localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    }
    
    // Mettre à jour l'état
    setDarkMode(isDarkMode);
    
    // Appliquer les classes CSS appropriées
    if (isDarkMode) {
      document.documentElement.classList.add('theme-dark');
      document.documentElement.classList.remove('theme-light');
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.classList.add('theme-light');
      document.documentElement.classList.remove('theme-dark');
      document.documentElement.setAttribute('data-theme', 'light');
    }
  }, []);

  // Fonction pour basculer le thème
  const toggleTheme = () => {
    if (darkMode) {
      // Passer au thème clair
      document.documentElement.classList.remove('theme-dark');
      document.documentElement.classList.add('theme-light');
      document.documentElement.setAttribute('data-theme', 'light');
      localStorage.setItem('theme', 'light');
    } else {
      // Passer au thème sombre
      document.documentElement.classList.remove('theme-light');
      document.documentElement.classList.add('theme-dark');
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('theme', 'dark');
    }
    
    // Mettre à jour l'état
    setDarkMode(!darkMode);
  };

  return (
    <button
      id="theme-switcher"
      onClick={toggleTheme}
      className="relative flex items-center justify-center rounded-full shadow transition-all duration-500 ease-in-out focus:outline-none overflow-hidden"
      style={{
        //backgroundColor: darkMode ? '#1f2937' : '#dbeafe',
        backgroundColor: darkMode ? 'var(--primary-dark)' : '#dbeafe',
        transform: 'scale(1)',
        transition: 'transform 0.2s, background-color 0.5s',
        height: '1.5rem',
        width: '1.5rem',
        marginRight: '.76rem',
        cursor: 'pointer',
        border: '2px solid var(--primary-color)'
      }}
      onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
      onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      aria-label={darkMode ? "Passer au thème clair" : "Passer au thème sombre"}
      role="switch"
      aria-checked={darkMode}
    >
      {/* Background glow effect */}
      <span 
        className="absolute inset-0 rounded-full"
        style={{
          backgroundColor: darkMode ? '#4f46e5' : '#fbbf24',
          opacity: darkMode ? 0.1 : 0.15,
          transition: 'background-color 0.5s, opacity 0.5s',
          filter: 'blur(4px)',
          transform: 'scale(0.85)'
        }}
      ></span>
           
      {/* Sun Icon (Material Icons) - Light Theme */}
      <span 
        className="absolute inset-0 flex items-center justify-center material-icons"
        style={{
          opacity: darkMode ? 0 : 1,
          position: 'absolute',
          transform: darkMode ? 'translateY(20px) scale(0.5)' : 'translateY(0) scale(1)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
          //color: '#ff9800',
          color: 'var(--primary-color)',
          fontSize: '1rem',
          pointerEvents: 'none'
        }}
      >
        light_mode
      </span>
      
      {/* Moon Icon (Material Icons) - Dark Theme */}
      <span 
        className="absolute inset-0 flex items-center justify-center material-icons"
        style={{
          opacity: darkMode ? 1 : 0,
          position: 'absolute',
          transform: darkMode ? 'translateY(0) scale(1)' : 'translateY(-20px) scale(0.5)',
          transition: 'opacity 0.5s ease, transform 0.5s ease',
          //color: '#e2e8f0',
          color: '#F2F2F2',
          fontSize: '1rem',
          pointerEvents: 'none'
        }}
      >
        dark_mode
      </span>
    </button>
  );
};

export default ThemeSwitcher;